import { lazy, Suspense, useState, type ComponentPropsWithRef, type Ref } from 'react';
import { getProductCardAttribute, ProductCard as ProductCardUI } from '@/features/product-card';
import { ProductButton } from '@/features/product-button';
import { ProductProvider } from '@/providers/product-context';
import { Link } from '@/components/link';
import { cn } from '@/lib/utils';
import { routeHref, routes } from '@/route-paths';
import { getPrimaryProductImageUrl } from '@/lib/product/product-utils';
import { getProductCardKind, getProductFromGridItem, type ProductCardItem } from '../../../lib/product-card-adapter';

const QuickBuy = lazy(() => import('@/features/quick-buy').then((module) => ({ default: module.QuickBuy })));

export type ProductCardProps = {
    product: ProductCardItem;
    priority?: boolean;
    onProductClick?: (product: ProductCardItem) => void;
    getProductUrl?: (product: ProductCardItem) => string;
} & Pick<ComponentPropsWithRef<'article'>, 'className'> & { ref?: Ref<HTMLElement> };

const linkFocusClassName = 'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring';

export const ProductCard = ({
    product: item,
    priority = false,
    onProductClick,
    getProductUrl,
    className,
    ref,
}: ProductCardProps) => {
    const product = getProductFromGridItem(item);
    const productUrl =
        getProductUrl?.(item) ??
        routeHref(routes.product, { productId: product.id } as {
            productId: string;
        });
    const kind = getProductCardKind(item);
    const attribute = getProductCardAttribute(product);
    const [selectedValue, setSelectedValue] = useState<string>();
    const [quickBuyOpen, setQuickBuyOpen] = useState(false);
    const [quickBuyMounted, setQuickBuyMounted] = useState(false);

    const shadeSelection =
        attribute?.kind === 'colour' && selectedValue ? { [attribute.id]: selectedValue } : undefined;
    const imageUrl = getPrimaryProductImageUrl(product, 'large', shadeSelection);

    const openQuickBuy = () => {
        setQuickBuyMounted(true);
        setQuickBuyOpen(true);
    };
    const handleProductClick = () => onProductClick?.(item);

    return (
        <ProductProvider product={product}>
            <ProductCardUI.Root ref={ref} className={cn('border-r border-b border-snk-greige', className)}>
                <ProductCardUI.Image
                    src={imageUrl}
                    priority={priority}
                    className="size-full"
                    imageClassName="object-contain">
                    {/* PLP badges stay bottom-left at every breakpoint (Figma). */}
                    <ProductCardUI.Badges className="md:top-auto md:bottom-2" />
                    {/* Pointer-only duplicate of the name link; keyboard and screen readers use the name link. */}
                    <Link
                        to={productUrl}
                        tabIndex={-1}
                        aria-hidden
                        data-slot="product-card-image-link"
                        className="col-start-1 row-start-1"
                        onClick={handleProductClick}
                    />
                    {/* Same 8px inset as the badges so the hover CTA fully covers them. */}
                    <ProductCardUI.Action className="md:px-2">
                        {/* Quick Buy is opened by the tile so it can preselect the swatch shade. */}
                        <ProductButton
                            kind={kind}
                            size="sm"
                            actionOverrides={{ 'quick-buy': openQuickBuy }}
                            data-slot="product-card-action-auto"
                            className="md:h-[38px] md:py-2"
                        />
                    </ProductCardUI.Action>
                </ProductCardUI.Image>
                <ProductCardUI.Details>
                    <Link
                        to={productUrl}
                        className={cn('flex w-full flex-col items-center gap-2', linkFocusClassName)}
                        onClick={handleProductClick}>
                        <ProductCardUI.Brand>{product.brand}</ProductCardUI.Brand>
                        <ProductCardUI.Name>{product.name ?? product.id}</ProductCardUI.Name>
                        <ProductCardUI.Price />
                    </Link>
                    {/* Figma tiles show shade swatches only; sizes are picked in Quick Buy. */}
                    {attribute?.kind === 'colour' && (
                        <ProductCardUI.Attribute onChange={setSelectedValue} onOverflowClick={openQuickBuy} />
                    )}
                    <ProductCardUI.Rating />
                    <ProductCardUI.Availability />
                </ProductCardUI.Details>
            </ProductCardUI.Root>
            {quickBuyMounted && (
                <Suspense fallback={null}>
                    <QuickBuy
                        productId={product.id}
                        currency={product.currency}
                        selectedColorValue={
                            attribute?.kind === 'colour' ? (selectedValue ?? attribute.initiallySelected) : undefined
                        }
                        open={quickBuyOpen}
                        onOpenChange={setQuickBuyOpen}
                    />
                </Suspense>
            )}
        </ProductProvider>
    );
};
